import React, { useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend,
  Label,
} from "recharts";
import { useBreakpointValue, Box, Text, Center } from "@chakra-ui/react";
import { Pt } from "./useNetSentimentSeries";

interface Props {
  data: Pt[];
  isChannelView?: boolean;
  selectedUserId?: string | null;
}

const CustomTooltip = ({ active, payload, label, isChannelView, onLock }: any) => {
  // Track which messages are expanded (keyed by user index and message index)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>({});

  if (!active || !payload || !payload.length) return null;

  const toggle = (key: string) => {
    setExpanded((s) => ({ ...s, [key]: !s[key] }));
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '8px',
      border: '1px solid #eee',
      borderRadius: '4px',
      fontSize: '12px',
      maxHeight: '200px',
      overflowY: 'auto',
      maxWidth: '420px',
      pointerEvents: 'auto'
    }}
      onMouseEnter={() => onLock?.(true)}
      onMouseLeave={() => onLock?.(false)}
    >
      {payload.map((entry: any, index: number) => {
        const isPositive = entry.dataKey === 'positive';
        const users = isChannelView ? (entry.payload.users?.[entry.dataKey] || []) : [];
        const value = Math.abs(entry.value);
        if (value === 0) return null;

        return (
          <div key={index} style={{
            color: isPositive ? '#38A169' : '#E53E3E',
            marginTop: index > 0 ? '4px' : '0'
          }}>
            {isChannelView ? (
              users.map((u: { userName: string; value: number; messages: string[] }, uidx: number) => (
                <div key={u.userName} style={{ marginBottom: '6px' }}>
                  <div style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {u.userName}: {u.value.toFixed(1)}
                  </div>
                  {/* {u.messages && u.messages.length > 0 && (
                    <ul style={{ marginTop: '2px', paddingLeft: '14px', color: '#4A5568' }}>
                      {(expandedUsers[`${uidx}`] ? u.messages : u.messages.slice(0, 3)).map((m, mi) => {
                        const key = `${uidx}-${mi}`;
                        const isOpen = !!expanded[key];
                        const truncated = m.length > 140 ? `${m.slice(0, 140)}…` : m;
                        return (
                          <li 
                            key={mi} 
                            style={{ cursor: m.length > 140 ? 'pointer' : 'default' }}
                            onClick={() => { if (m.length > 140) toggle(key); }}
                          >
                            <span
                              style={isOpen ? { whiteSpace: 'normal' } : {
                                display: 'inline-block',
                                maxWidth: '360px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              title={m.length > 140 ? 'Click to expand' : undefined}
                            >
                              {isOpen ? m : truncated}
                            </span>
                          </li>
                        );
                      })}
                      {u.messages.length > 3 && (
                        <li style={{ marginTop: '4px' }}>
                          <span
                            style={{ color: '#2B6CB0', cursor: 'pointer', fontWeight: 600 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedUsers((s) => ({ ...s, [`${uidx}`]: !s[`${uidx}`] }));
                            }}
                          >
                            {expandedUsers[`${uidx}`] ? 'Show less' : `Show all (${u.messages.length - 3} more)`}
                          </span>
                        </li>
                      )}
                    </ul>
                  )} */}
                </div>
              ))
            ) : (
              <div style={{ whiteSpace: 'nowrap' }}>
                {value.toFixed(1)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const NetSentimentWave: React.FC<Props> = ({ data, isChannelView = false, selectedUserId = null }) => {
  const height = useBreakpointValue({ base: 150, md: 175, lg: 200 })!;
  const [pinned, setPinned] = useState(false);
  const [tt, setTt] = useState<any>({ active: false, payload: null, label: null });

  // If in user view and no user selected, show message
  if (!isChannelView && !selectedUserId) {
    return (
      <Center height={height} bg="rgba(255,255,255,0.02)" borderRadius="md" border="1px dashed" borderColor="rgba(203,213,225,0.25)">
        <Text color="gray.400" fontSize="sm">
          Please select a user to view their sentiment analysis
        </Text>
      </Center>
    );
  }

  const chartData = data.map((d) => ({
    label: d.label,
    positive: d.positiveValue || 0,
    negative: d.negativeValue || 0,
    users: d.users || { positive: [], negative: [] }
  }));

  const noSignal = !chartData.length || chartData.every(
    (d) => d.positive === 0 && d.negative === 0
  );

  if (noSignal) {
    return (
      <Center height={height} bg="rgba(255,255,255,0.02)" borderRadius="md" border="1px dashed" borderColor="rgba(203,213,225,0.25)">
        <Text color="gray.400" fontSize="sm">
          No sentiment data available for this {isChannelView ? 'channel' : 'user'}
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 24, bottom: 0 }}
          onMouseMove={(state: any) => {
            if (!pinned) setTt({ active: true, payload: state?.activePayload, label: state?.activeLabel });
          }}
          onMouseLeave={() => { if (!pinned) setTt({ active: false, payload: null, label: null }); }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            domain={[-5, 5]}
            ticks={[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5]}
            tickFormatter={(value) => value.toFixed(1)}
          >
            <Label
              value="Sentiment Score"
              position="insideLeft"
              angle={-90}
              style={{ textAnchor: 'middle' }}
            />
          </YAxis>
          <ReferenceLine y={0} stroke="#CBD5E0" strokeWidth={2} />

          <Area
            type="monotone"
            dataKey="negative"
            fill="#FED7D7"
            fillOpacity={0.3}
            stroke="#E53E3E"
            strokeWidth={2}
            baseValue={0}
          />
          <Area
            type="monotone"
            dataKey="positive"
            fill="#C6F6D5"
            fillOpacity={0.3}
            stroke="#38A169"
            strokeWidth={2}
            baseValue={0}
          />

          <Tooltip
            active={pinned || tt.active}
            payload={tt.payload}
            label={tt.label}
            content={(props) => <CustomTooltip {...props} isChannelView={isChannelView} onLock={(v: boolean) => setPinned(v)} />}
            wrapperStyle={{ pointerEvents: 'auto' }}
            allowEscapeViewBox={{ x: true, y: true }}
            position={{ y: 10 }}
          />

          <Legend
            verticalAlign="top"
            payload={[
              { value: "Positive Messages", type: "square", color: "#38A169" },
              { value: "Negative Messages", type: "square", color: "#E53E3E" },
            ]}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default NetSentimentWave;
